import type {

  GetItemsDto
} from '@mimir/common/dto/itemModels';
import type { ApiClient } from '@mimir/common/util/apiClient';
import { apiGet } from '@mimir/common/util/http';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import { ofItems } from '../aws/api/path';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { assumeResultArray } from '@mimir/common/client/assumeResult';
import { injectDefaultQueryParamsForItemApis } from './injectItemQueryParams';

/*
  Manual testing seems to indicate that around 4260 and above, the requests
  starts failing due to too large URLs.
*/
const ID_LIST_SIZE_LIMIT = 4000;

function* splitIds(ids: string[]) {
  let batch: string[] = [];
  let length = 2;
  for (const id of ids) {
    const cost = id.length + 3;
    if (batch.length > 0 && length + cost > ID_LIST_SIZE_LIMIT) {
      yield batch;
      batch = [];
      length = 2;
    }
    batch.push(id);
    length += cost;
  }

  if (batch.length > 0)
    yield batch;
}

async function getItemsBatch(client: ApiClient, pathOfItems: string, idBatch: string[], fields: string | undefined): Promise<GetItemsDto[]> {
  const queryParams: Record<string, string> = {
      // Since item IDs could be virtually any non empty string,
      // it would not be safe to use plain string concatenation to
      // turn them into a query parameter.
      // Since the AWS API gateway client does not support multivalue query
      // parameters, we turn the array into a proper string to avoid
      // escaping problems.
      ids: JSON.stringify(idBatch)
  };

  // Note: Assigning undefined is not equivalent to not assigning anything.
  if (fields) {
    queryParams.fields = fields;
  }

  const result = await apiGet(client, pathOfItems, injectDefaultQueryParamsForItemApis({ queryParams }));
  return assumeResultArray<any>(result);
}

export const api_getItems = async <Key extends keyof (GetItemsDto)>(
  c: BackendClient, itemIds: string[], fields: Key[] | readonly Key[] = []
): Promise<Pick<GetItemsDto, Key | 'thumbnail'>[]> => {

  const pathOfItems = ofItems(c.getServiceLinks());
  const commaFields = isEmpty(fields) ? undefined : fields.join(',');

  const promises: Promise<GetItemsDto[]>[] = [];
  for (const idBatch of splitIds(itemIds)) {
    promises.push(getItemsBatch(c.getApiClient(), pathOfItems, idBatch, commaFields));
  }

  if (promises.length > 1) {
    console.warn(`getItemsApi: ${promises.length} requests needed for ${itemIds.length} items`);
  }

  const responses: GetItemsDto[][] = await Promise.all(promises);
  return flatten(responses);
};

